import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useWorkspace } from '../context/WorkspaceContext';
import { useHubData, CardError, CardEmpty } from '../components/dashboard-hub/DateRangeSelect';
import { RESOURCE_ICONS, timeAgo } from '../components/dashboard-hub/RightRail';

export default function ActivityLog() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const [page, setPage] = useState(1);
  const url = wsId ? `/api/dashboard-hub/activity-log?workspace_id=${wsId}&page=${page}&limit=25` : null;
  const { data, loading, error, retry } = useHubData(url);

  return (
    <div className="min-h-full bg-[#F8FAFC] px-4 py-6 sm:px-6" data-testid="activity-log-page">
      <div className="mx-auto max-w-3xl">
        <button type="button" onClick={() => navigate('/dashboard')} data-testid="activity-back-btn"
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#657085] hover:text-[#172033]">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>
        <div className="rounded-2xl border border-[#E5EAF0] bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-[#172033]">Business Activity</h1>
            {data && <p className="text-xs text-[#657085]" data-testid="activity-total">{data.total} events</p>}
          </div>
          {loading ? (
            <div className="mt-4 space-y-3">{[...Array(8)].map((_, i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />)}</div>
          ) : error ? (
            <CardError onRetry={retry} />
          ) : !data?.items?.length ? (
            <CardEmpty label="No activity recorded yet for this business." />
          ) : (
            <>
              <ul className="mt-3 divide-y divide-[#EEF2F6]">
                {data.items.map((item) => {
                  const Icon = RESOURCE_ICONS[(item.resource || '').toLowerCase()] || Activity;
                  return (
                    <li key={item.id} className="flex items-start gap-3 py-3" data-testid="activity-log-item">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#20B94B]/10">
                        <Icon className="h-4 w-4 text-[#118A35]" aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm capitalize text-[#172033]">{item.title}</p>
                        <p className="text-xs text-[#657085]">{item.actor_name}</p>
                      </div>
                      <span className="shrink-0 text-xs text-[#657085]">{timeAgo(item.at)}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-4 flex items-center justify-between border-t border-[#EEF2F6] pt-4">
                <Button variant="outline" size="sm" disabled={page <= 1} data-testid="activity-prev-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="mr-1 h-4 w-4" /> Previous
                </Button>
                <span className="text-xs text-[#657085]" data-testid="activity-page-label">
                  Page {data.page} of {data.pages}
                </span>
                <Button variant="outline" size="sm" disabled={page >= data.pages} data-testid="activity-next-btn"
                  onClick={() => setPage((p) => p + 1)}>
                  Next <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
